import type { McArtifact, McEvent, McFindingDetail } from "../../lib/mc";
import { evidenceKeys } from "./runData";

type CsvValue = string | number | null | undefined;

const csvCell = (value: CsvValue): string => {
  let cell = value === null || value === undefined ? "" : String(value);
  // A leading = + - or @ is a formula when the file is opened in a spreadsheet.
  if (/^[=+\-@\t\r]/.test(cell)) cell = `'${cell}`;
  return /[",\r\n]|^\s|\s$/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
};

/** RFC 4180 CSV: cells with commas, quotes, line breaks or edge whitespace are quoted, quotes doubled. */
export const toCsv = (rows: CsvValue[][]): string => rows.map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";

const FINDING_COLUMNS = ["id", "severity", "kind", "title", "mission", "recorded_at", "description", "repro", "evidence"];

export const findingsCsv = (findings: McFindingDetail[], artifacts: McArtifact[] = []): string =>
  toCsv([
    FINDING_COLUMNS,
    ...findings.map((f) => [
      f.id,
      f.severity,
      f.kind,
      f.title,
      f.missionId,
      f.recordedAt,
      f.description,
      f.repro,
      evidenceKeys(f, artifacts).join(" "),
    ]),
  ]);

export const runFilePrefix = (runId: string) => `mc-run-${runId.slice(0, 8)}`;

export const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const saveText = (text: string, filename: string, type: string) =>
  saveBlob(new Blob([text], { type }), filename);

export const saveFindingsCsv = (runId: string, findings: McFindingDetail[], artifacts?: McArtifact[]) =>
  saveText(findingsCsv(findings, artifacts), `${runFilePrefix(runId)}-findings.csv`, "text/csv;charset=utf-8");

export const saveEventJson = (runId: string, event: McEvent) =>
  saveText(JSON.stringify(event, null, 2), `${runFilePrefix(runId)}-event-${event.seq}.json`, "application/json");
